import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, Calendar, Shield, LogOut, MapPin, Sun, MessageCircle, MessageCircleMore, Phone, LayoutDashboard, BookOpen, Settings } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAssistant } from '../AssistantDock';
import { taskApi } from '../../services/api';
import ScopeControl from './ScopeControl';

type NavItem = {
  group?: string;
  to?: string;
  label?: string;
  Icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  badge?: 'proposed';
  adminOnly?: boolean;
};

const NAV: NavItem[] = [
  { group: 'Your day' },
  { to: '/today', label: 'Today', Icon: Sun, badge: 'proposed' },
  { to: '/calls', label: 'Calls', Icon: Phone },
  { to: '/messages', label: 'Messages', Icon: MessageCircle },
  { to: '/leads', label: 'Leads', Icon: Users },
  { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { group: 'Team' },
  { to: '/queue', label: 'Queue', Icon: Calendar },
  { to: '/visits', label: 'Visits', Icon: MapPin },
  { to: '/users', label: 'Users', Icon: Shield, adminOnly: true },
  { group: 'You' },
  { to: '/training', label: 'Training', Icon: BookOpen },
  { to: '/settings', label: 'Settings', Icon: Settings },
];

const Sidebar: React.FC<{ onJump: () => void }> = ({ onJump }) => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  // The one badge worth a fetch: your AI-suggested tasks are invisible
  // everywhere else until you triage them.
  const [proposed, setProposed] = useState(0);
  const assistant = useAssistant();

  useEffect(() => {
    taskApi.list({ status: 'proposed' }).then((r) => setProposed(r.total));
  }, [pathname]);

  const isActive = (to: string) => pathname === to || pathname.startsWith(to + '/');

  return (
    <div className="w-60 flex-none h-full flex flex-col bg-warm-white border-r-[1.5px] border-sketch">
      <Link
        to="/today"
        className="flex flex-col items-start gap-2.5 px-5 py-5 border-b-[1.5px] border-dashed border-sketch-soft"
      >
        <img src="/logo.png" alt="Bhuvana Fortune Greens" className="h-11 w-auto" />
        <span className="font-display text-[19px] text-forest font-semibold tracking-tight leading-none">
          Nurture
        </span>
      </Link>

      <button
        onClick={onJump}
        className="mx-3 mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg border-[1.5px] border-sketch
                   bg-cream text-[12.5px] text-graphite hover:text-charcoal"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} className="w-3.5 h-3.5">
          <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
        </svg>
        Jump to a lead…
        <kbd className="ml-auto font-display text-[10.5px] border-[1.5px] border-sketch rounded px-1.5 bg-warm-white">
          ⌘K
        </kbd>
      </button>

      <ScopeControl />

      <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto">
        {NAV.map((item, i) => {
          if (item.group) {
            return (
              <div
                key={`g${i}`}
                className="font-display text-[10px] font-semibold tracking-[0.13em] uppercase
                           text-graphite px-3 pt-3 pb-0.5 opacity-80"
              >
                {item.group}
              </div>
            );
          }
          if (item.adminOnly && user?.role !== 'admin') return null;
          const { to, label, badge } = item;
          const Icon = item.Icon!;
          const active = isActive(to!);
          return (
            <Link
              key={to}
              to={to!}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg border-[1.5px] text-[13.5px]
                          font-bold transition-transform ${
                active
                  ? 'bg-sage/20 border-sketch text-forest shadow-sketch-btn -rotate-[0.35deg]'
                  : 'border-transparent text-graphite hover:bg-cream hover:text-charcoal'
              }`}
            >
              <Icon className="w-[19px] h-[19px] flex-none" strokeWidth={1.75} />
              {label}
              {badge === 'proposed' && proposed > 0 && (
                <span className="num ml-auto text-[11px] font-bold px-1.5 rounded-full bg-gold text-charcoal">
                  {proposed}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {assistant.available && (
        <div className="px-3 pb-3">
          <button
            onClick={assistant.toggle}
            aria-pressed={assistant.mode !== 'closed'}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg border-[1.5px] text-[13.5px]
                        font-bold transition-transform ${
              assistant.mode !== 'closed'
                ? 'bg-sage/20 border-sketch text-forest shadow-sketch-btn -rotate-[0.35deg]'
                : 'border-transparent text-forest hover:bg-cream'
            }`}
          >
            <MessageCircleMore className="w-[19px] h-[19px] flex-none" strokeWidth={1.75} />
            Assistant
            <span
              title={assistant.online === null ? 'Connecting' : assistant.online ? (assistant.busy ? 'Engine busy' : 'Engine online') : 'Engine offline'}
              className={`ml-auto w-2 h-2 rounded-full ${
                assistant.online === null ? 'bg-charcoal/20' : assistant.online ? (assistant.busy ? 'bg-gold' : 'bg-forest') : 'bg-brick'
              }`}
            />
          </button>
        </div>
      )}
      <div className="p-4 border-t-[1.5px] border-dashed border-sketch-soft">
        <div className="flex items-center gap-2.5 mb-3">
          {user?.picture
            ? <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full flex-none" />
            : <span className="avat w-9 h-9 bg-forest text-warm-white text-sm">
                {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
              </span>}
          <span className="min-w-0">
            <span className="block text-[13px] font-bold truncate">{user?.name || user?.email}</span>
            {user?.name && <span className="block text-[11px] text-graphite truncate">{user.email}</span>}
          </span>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 py-1.5 rounded-lg border-[1.5px]
                     border-sketch text-[12.5px] font-bold text-graphite hover:text-forest
                     hover:border-forest hover:-translate-y-px transition-transform"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.75} />
          Sign out
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
